// Per-organisation module feature toggles.
// Adding a new module here is forward-compatible: missing keys default to ON.

export type ModuleKey =
  | "assessment"
  | "services"
  | "marketplace"
  | "experts"
  | "funding";

export type ModuleConfig = Partial<Record<ModuleKey, boolean>>;

export const ALL_MODULES: ModuleKey[] = [
  "assessment",
  "services",
  "marketplace",
  "experts",
  "funding",
];

export const DEFAULT_MODULE_CONFIG: Record<ModuleKey, boolean> = {
  // Assessment defaults to OFF — admins enable per-organisation during pilot.
  assessment: false,
  services: true,
  marketplace: true,
  experts: true,
  funding: true,
};

export const MODULE_LABELS: Record<ModuleKey, string> = {
  assessment: "Age-Friendly Index",
  services: "Services",
  marketplace: "Care and Wellbeing Marketplace",
  experts: "Experts",
  funding: "Testbed",
};

export const MODULE_DESCRIPTIONS: Record<ModuleKey, string> = {
  assessment:
    "AI-powered benchmarking system for evaluating age-friendly communities across 10 dimensions.",
  services:
    "Browse and request services across the SAFE pillars.",
  marketplace:
    "Discover verified solution vendors and shortlist offerings for your team.",
  experts:
    "Book advisory sessions with vetted domain experts.",
  funding:
    "Testbed supports the development, testing, and scaling of age-friendly solutions through collaborative ecosystems.",
};

/**
 * Returns true when the module is enabled for the given config.
 * - No org / no config → enabled (solo users keep everything).
 * - Missing key → falls back to the project default.
 * - Explicit `false` → disabled.
 */
export function isModuleEnabled(
  config: ModuleConfig | null | undefined,
  key: ModuleKey,
): boolean {
  if (!config) return true;
  const v = config[key];
  if (v === undefined) return DEFAULT_MODULE_CONFIG[key];
  return v !== false;
}

/**
 * Assessment-specific access: respects both the toggle AND optional expiry.
 * Solo users (no org) keep access — they have nothing to administer against.
 */
export function isAssessmentAccessActive(
  org: {
    module_config: ModuleConfig | null;
    assessment_access_expires_at: string | null;
    assessment_access_source?: string | null;
  } | null,
): {
  enabled: boolean;
  expiresAt: string | null;
  expired: boolean;
  isDemo: boolean;
} {
  if (!org) {
    return { enabled: true, expiresAt: null, expired: false, isDemo: false };
  }
  const toggleOn = isModuleEnabled(org.module_config, "assessment");
  const expiresAt = org.assessment_access_expires_at;
  const expired =
    !!expiresAt && new Date(expiresAt).getTime() <= Date.now();
  return {
    enabled: toggleOn && !expired,
    expiresAt,
    expired: toggleOn && expired,
    isDemo: org.assessment_access_source === "demo",
  };
}
